import './leasing-info.scss';

import { useContext, useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { Input, Select } from '@/components/Shared/ui';
import { DictTypes } from '@/constants';
import { setDictionaryOptions } from '@/services';
import { formatPrice } from '@/services/formatter';
import { useAppSelector } from '@/store';

import { CreateTabContext } from '../../LlpOrder';
import type { CreateLeasingProps } from '../../widgets/create-tab';

const LeasingInfo: React.FC = () => {
  const { isFieldsFrozen } = useContext(CreateTabContext);
  const { dicts } = useAppSelector((state) => state.dict);

  const {
    control,
    setValue,
    resetField,
    formState: { errors }
  } = useFormContext<CreateLeasingProps>();

  const vehicles = useWatch({ control, name: 'vehicles' });
  const leasingAmount = (vehicles ?? []).reduce(
    (amount, vehicle) =>
      amount + Number(vehicle?.price ?? 0) * Number(vehicle?.count ?? 0),
    0
  );
  useEffect(() => {
    if (isFieldsFrozen) return;
    if (leasingAmount) resetField('leasingAmount');
    setValue('leasingAmount', leasingAmount);
  }, [leasingAmount, isFieldsFrozen]);

  const leasingPeriod = useMemo(
    () =>
      setDictionaryOptions(DictTypes.LoanTerms, dicts)?.map((item) => ({
        value: String(item.label),
        label: String(item.label)
      })) || [],
    [dicts]
  );

  return (
    <div className="leasing-info">
      <Controller
        control={control}
        name="leasingAmount"
        rules={{
          required: 'Пожалуйста, введите цену авто',
          min: { value: 1, message: 'Пожалуйста, введите цену авто' }
        }}
        render={({ field }) => (
          <Input
            {...field}
            type="tel"
            value={formatPrice(field.value)}
            onChange={() => null}
            label="Сумма лизинга"
            isRequired
            error={errors.leasingAmount?.message || ''}
            isFrozen
          />
        )}
      />
      <Controller
        control={control}
        name="initialPayment"
        rules={{
          required: 'Пожалуйста, введите данные',
          min: { value: 1, message: 'Пожалуйста, введите данные' }
        }}
        render={({ field }) => (
          <Input
            {...field}
            type="tel"
            value={formatPrice(field.value)}
            onChange={(e) => {
              const value = e.target.value.replace(/\s/g, '');
              if (value.match(/\D/) || value.length > '10111222'.length) return;
              field.onChange(+value);
            }}
            label="Первоначальный взнос"
            isRequired
            error={errors.initialPayment?.message || ''}
            isFrozen={isFieldsFrozen}
          />
        )}
      />
      <Controller
        control={control}
        name="leasingPeriod"
        rules={{
          required: 'Пожалуйста, выберите значение',
          min: { value: 1, message: 'Пожалуйста, выберите значение' }
        }}
        render={({ field }) => (
          <Select
            {...field}
            selectedValue={leasingPeriod.find(
              (item) => Number(item.value) === field.value
            )}
            onChange={(value) => {
              field.onChange(Number(value.value));
            }}
            options={leasingPeriod}
            label="Срок лизинга"
            isRequired
            error={errors.leasingPeriod?.message || ''}
            isFrozen={isFieldsFrozen}
          />
        )}
      />
    </div>
  );
};

export default LeasingInfo;
